import { useState, useEffect } from 'react';
import { Globe, Plus, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import { dnsService } from '../services/dns.service';
import { useTenantStore } from '../stores/tenantStore';
import type { DnsRecord, DnsServiceType, DnsSlugInfo, CreateDnsRecordData } from '../services/dns.service';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useToast } from '../components/common/ToastContainer';
import { usePermissions } from '../hooks/usePermissions';

const StatusDot = ({ status }: { status: string }) => {
  const color = status === 'online' ? 'bg-green-500' : status === 'offline' ? 'bg-gray-400' : 'bg-orange-400';
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
};

const SourceBadge = ({ source }: { source: string }) => {
  const colors = {
    auto_device: 'bg-blue-100 text-blue-700',
    auto_connector: 'bg-purple-100 text-purple-700',
    manual: 'bg-gray-100 text-gray-700',
  };
  const labels = { auto_device: 'Device', auto_connector: 'Connector', manual: 'Manual' };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[source as keyof typeof colors] || colors.manual}`}>
      {labels[source as keyof typeof labels] || source}
    </span>
  );
};

const DnsRecords = () => {
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [activeTab, setActiveTab] = useState<'records' | 'types' | 'settings'>('records');
  const [records, setRecords] = useState<DnsRecord[]>([]);
  const [serviceTypes, setServiceTypes] = useState<DnsServiceType[]>([]);
  const [slugInfo, setSlugInfo] = useState<DnsSlugInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Create record modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateDnsRecordData>({ hostname: '', ipAddress: '' });
  const [saving, setSaving] = useState(false);

  // Slug edit
  const [editingSlug, setEditingSlug] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [showSlugConfirm, setShowSlugConfirm] = useState(false);

  // Delete
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [recs, types, slug] = await Promise.all([
        dnsService.listRecords(search || undefined),
        dnsService.listServiceTypes(),
        dnsService.getSlug(),
      ]);
      setRecords(recs);
      setServiceTypes(types);
      setSlugInfo(slug);
    } catch (err: any) {
      toast.error('Error', err.message || 'Failed to load DNS data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [search, selectedTenantId]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await dnsService.createRecord(createForm);
      toast.success('Created', 'DNS record created');
      setShowCreateModal(false);
      setCreateForm({ hostname: '', ipAddress: '' });
      await fetchAll();
    } catch (err: any) {
      // VSNP-1020: surface the API's message, not axios's. The duplicate
      // hostname rejection ("Hostname \"x\" already registered in this
      // tenant") lives in err.response.data.message; err.message alone is
      // "Request failed with status code 400", which tells the operator
      // nothing about what to fix.
      toast.error('Error', err.response?.data?.message || err.message || 'Failed to create DNS record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await dnsService.deleteRecord(deleteId);
      toast.success('Deleted', 'DNS record removed');
      setDeleteId(null);
      await fetchAll();
    } catch (err: any) {
      toast.error('Error', err.message);
    }
  };

  const handleSlugUpdate = async () => {
    setSaving(true);
    try {
      await dnsService.updateSlug(newSlug);
      toast.success('Updated', 'DNS slug updated. All edges will be re-synced.');
      setShowSlugConfirm(false);
      setEditingSlug(false);
      await fetchAll();
    } catch (err: any) {
      toast.error('Error', err.message);
    } finally {
      setSaving(false);
    }
  };

  const stMap = new Map(serviceTypes.map(st => [st.id, st]));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Globe className="w-6 h-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Private DNS</h1>
          {slugInfo?.slug && (
            <code className="text-sm font-mono bg-primary-50 text-primary-700 px-2 py-1 rounded">
              *.{slugInfo.slug}.{slugInfo.zoneSuffix}
            </code>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={fetchAll} className="btn-secondary inline-flex items-center space-x-1">
            <RefreshCw className="w-4 h-4" />
          </button>
          {canCreate && (
          <button onClick={() => setShowCreateModal(true)} className="btn-primary inline-flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Record</span>
          </button>
          )}
        </div>
      </div>

      {/* IKEv2 + Tailscale compatibility notice */}
      <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">IKEv2 VPN + Tailscale compatibility</p>
          <p className="mt-1">
            Clients using IKEv2 with Tailscale running will not auto-resolve private DNS names.
            Tailscale's Network Extension overrides IKEv2 DNS on macOS/iOS regardless of domain configuration.
            Affected users must either configure{' '}
            <strong>Tailscale Split DNS</strong> to forward <code className="bg-amber-100 px-1 rounded">{slugInfo?.zoneSuffix || 'sl.internal'}</code>{' '}
            to the edge DNS server, use <strong>WireGuard</strong> protocol instead, or disable Tailscale.
            Direct queries (<code className="bg-amber-100 px-1 rounded">dig @gateway</code>) are unaffected.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b">
        {(['records', 'types', 'settings'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'records' ? 'DNS Records' : tab === 'types' ? 'Service Types' : 'Settings'}
          </button>
        ))}
      </div>

      {/* Records Tab */}
      {activeTab === 'records' && (
        <div className="card">
          <div className="p-4 border-b">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by hostname..."
              className="input-base w-full max-w-sm"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hostname</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">FQDN</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                ) : records.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No DNS records found</td></tr>
                ) : records.map(record => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3"><StatusDot status={(record as any).status || 'unknown'} /></td>
                    <td className="px-4 py-3 font-medium text-gray-900">{record.hostname}</td>
                    <td className="px-4 py-3 font-mono text-sm text-gray-600">{record.fqdn}</td>
                    <td className="px-4 py-3 font-mono text-sm">{record.ip_address}</td>
                    <td className="px-4 py-3 text-sm">
                      {record.service_type_id && stMap.has(record.service_type_id)
                        ? stMap.get(record.service_type_id)!.display_name
                        : '—'}
                      {record.service_port && <span className="text-gray-400 ml-1">:{record.service_port}</span>}
                    </td>
                    <td className="px-4 py-3"><SourceBadge source={record.source} /></td>
                    <td className="px-4 py-3">
                      {canDelete && (
                      <button onClick={() => setDeleteId(record.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Service Types Tab */}
      {activeTab === 'types' && (
        <div className="card p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {serviceTypes.map(st => (
              <div key={st.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{st.display_name}</span>
                  {st.is_system ? (
                    <span className="text-xs text-gray-400">System</span>
                  ) : (
                    canDelete && (
                    <button onClick={() => dnsService.deleteServiceType(st.id).then(fetchAll)}
                      className="text-red-400 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    )
                  )}
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  {st.protocol && <p>Protocol: {st.protocol.toUpperCase()}</p>}
                  {st.default_port && <p>Port: {st.default_port}</p>}
                  {st.url_scheme && <p>Scheme: {st.url_scheme}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="card p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Tenant DNS Slug</h3>
            <p className="text-sm text-gray-500 mb-3">
              The slug is used in all FQDNs: <code>hostname.<strong>{slugInfo?.slug || 'your-slug'}</strong>.{slugInfo?.zoneSuffix || 'sl.internal'}</code>
            </p>
            {editingSlug ? (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newSlug}
                  onChange={e => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className="input-base w-64 font-mono"
                  placeholder="my-company"
                />
                <button onClick={() => setShowSlugConfirm(true)} className="btn-primary text-sm" disabled={!newSlug || newSlug === slugInfo?.slug}>
                  Save
                </button>
                <button onClick={() => { setEditingSlug(false); setNewSlug(''); }} className="btn-secondary text-sm">
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <code className="text-lg font-mono font-semibold text-primary-700 bg-primary-50 px-3 py-1 rounded">
                  {slugInfo?.slug || 'Not set'}
                </code>
                {canUpdate && (
                  <button
                    onClick={() => { setEditingSlug(true); setNewSlug(slugInfo?.slug || ''); }}
                    className="text-sm text-gray-500 hover:text-primary-600 underline"
                  >
                    Change
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Record Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Register DNS Endpoint"
        size="lg"
        footer={
          <div className="flex justify-end space-x-3">
            <button onClick={() => setShowCreateModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleCreate} disabled={saving || !createForm.hostname || !createForm.ipAddress} className="btn-primary">
              {saving ? 'Creating...' : 'Create'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hostname</label>
            <input
              type="text"
              value={createForm.hostname}
              onChange={e => setCreateForm({ ...createForm, hostname: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
              placeholder="e.g., obp-portal"
              className="input-base w-full"
            />
            {slugInfo?.slug && createForm.hostname && (
              <p className="mt-1 text-xs text-gray-500 font-mono">
                → {createForm.hostname}.{slugInfo.slug}.{slugInfo.zoneSuffix}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
            <input
              type="text"
              value={createForm.ipAddress}
              onChange={e => setCreateForm({ ...createForm, ipAddress: e.target.value })}
              placeholder="e.g., 100.64.200.2"
              className="input-base w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
            <select
              value={createForm.serviceTypeId || ''}
              onChange={e => setCreateForm({ ...createForm, serviceTypeId: e.target.value || undefined })}
              className="input-base w-full"
            >
              <option value="">None</option>
              {serviceTypes.map(st => (
                <option key={st.id} value={st.id}>{st.display_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Port (optional)</label>
            <input
              type="number"
              value={createForm.servicePort || ''}
              onChange={e => setCreateForm({ ...createForm, servicePort: parseInt(e.target.value) || undefined })}
              placeholder="e.g., 3020"
              className="input-base w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <input
              type="text"
              value={createForm.description || ''}
              onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
              placeholder="e.g., OBP Call Center Portal"
              className="input-base w-full"
            />
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete DNS Record"
        message="This will remove the DNS record. Connected VPN clients will no longer be able to resolve this hostname."
        variant="danger"
      />

      {/* Slug Change Confirm */}
      <ConfirmDialog
        isOpen={showSlugConfirm}
        onClose={() => setShowSlugConfirm(false)}
        onConfirm={handleSlugUpdate}
        title="Change DNS Slug"
        message={`WARNING: Changing the DNS slug will:\n\n• Update ALL FQDNs for this tenant\n• Re-sync ALL edge devices\n• All VPN clients must reconnect\n• Existing bookmarks/shortcuts will break\n\nChange slug to "${newSlug}"?`}
        confirmText="Yes, Change Slug"
        variant="danger"
        isLoading={saving}
      />
    </div>
  );
};

export default DnsRecords;
